import React, { useCallback, useEffect, useMemo, useState } from "react";
import sumBy from "lodash/sumBy";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client/react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router";

import Dimensions from "../../../../views/inventory/requests/Dimensions";
import { LOCATION_SNAPSHOT_ASSETS_QUERY } from "../../../../constants/graphql/queries";
import {
  CREATE_INVENTORY_TRANSACTION_MUTATION,
  ASSETS_BULK_REMOVE_MUTATION,
} from "../../../../constants/graphql/mutations";
import { ProjectInventoryProvider } from "../../../../contexts/projectInventory";
import { useWarehouseContext } from "../../../../views/warehouse/Warehouse";
import useMutation from "../../../../hooks/useMutation";
import useNotification from "../../../../hooks/notification";
import useQueryParams from "../../../../hooks/useQueryParams";
import LocationSnapshotMobileView from "./LocationSnapshotMobileView";
import LocationSnapshotAddItemModal from "./LocationSnapshotAddItemModal/index";
import LocationSnapshotEditQuantityModal from "./LocationSnapshotEditQuantityModal";
import LocationSnapshotBulkChangeQuantityModal from "./LocationSnapshotBulkChangeQuantityModal";
import ConfirmDeleteModal from "../../../images/ConfirmDeleteModal";
import { LOCATION_SNAPSHOT_ADD_ITEM_STEPS } from "./LocationSnapshotAddItemModal/steps";
import LabelingOptionsModal from "../../../modals/LabelingOptionsModal";
import AssetLabelingOptionsModal from "../../../modals/AssetLabelingOptionsModal";
import useItemLabelsModal from "../../../../hooks/useItemLabelsModal";

const getLocationName = (location) => location?.location_name || location?.locationName;
const getLocationId = (location) => location?.location_id || location?.locationId || location?.id;
const getWarehouseId = (warehouse) => warehouse?.warehouse_id || warehouse?.warehouseId;
const getAssetIdentifier = (asset) =>
  asset?.itemId || asset?.assetId || asset?.id || "Unknown item";

const formatMutationErrors = (errors) => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return "";
  }

  return errors
    .map((error) => error?.message)
    .filter(Boolean)
    .join(", ");
};

export default function LocationSnapshot({
  locationName,
  isMobileLocation,
  entity,
  locationDetails,
  onOpenAsset,
  onRefetchLocationDetails,
  auditModeEnabled: controlledAuditMode,
  setAuditModeEnabled: setControlledAuditMode,
  auditModeLoading = false,
}) {
  const warehouseId = entity.warehouseId;
  const { refetchWarehouse } = useWarehouseContext();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { notifySuccess, notifyError } = useNotification();
  const [createInventoryTransaction] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const [bulkRemoveAssets] = useMutation(ASSETS_BULK_REMOVE_MUTATION);
  const navigate = useNavigate();
  const queryParams = useQueryParams();

  const openAddItem = queryParams.has("addItemStep");

  const openAddItemModal = useCallback(() => {
    const params = new URLSearchParams(queryParams.toString());
    params.set("addItemStep", LOCATION_SNAPSHOT_ADD_ITEM_STEPS.CHOICE);
    navigate({ search: params.toString() });
  }, [navigate, queryParams]);

  const closeAddItemModal = useCallback(() => {
    const params = new URLSearchParams(queryParams.toString());
    params.delete("addItemStep");
    navigate({ search: params.toString() });
  }, [navigate, queryParams]);

  const [internalAuditMode, setInternalAuditMode] = useState(false);
  const auditModeEnabled =
    controlledAuditMode !== undefined ? controlledAuditMode : internalAuditMode;
  const setAuditModeEnabled =
    setControlledAuditMode !== undefined ? setControlledAuditMode : setInternalAuditMode;
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);
  const [openRemoveConfirmation, setOpenRemoveConfirmation] = useState(false);
  const [openDeleteAssetConfirmation, setOpenDeleteAssetConfirmation] = useState(false);
  const [isDeletingAssets, setIsDeletingAssets] = useState(false);
  const [deletedAssetIds, setDeletedAssetIds] = useState([]);
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState(null);
  const [openBulkChangeQuantityModal, setOpenBulkChangeQuantityModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isBulkChangingQuantity, setIsBulkChangingQuantity] = useState(false);
  const [removeProgress, setRemoveProgress] = useState({ current: 0, total: 0 });
  const [editQuantityAsset, setEditQuantityAsset] = useState(null);
  const [isSavingQuantity, setIsSavingQuantity] = useState(false);
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const itemLabelsModal = useItemLabelsModal();

  const handleOpenLabelModal = useCallback(asset => {
    const shipment = asset.shipmentItems?.[0]?.shipment;
    if (shipment?.id) {
      itemLabelsModal.openForShipment(shipment.id, asset.shipmentItems[0].id);
    } else {
      itemLabelsModal.openForAsset(asset.id);
    }
  }, [itemLabelsModal.openForShipment, itemLabelsModal.openForAsset]);

  const {
    data: { assetsCollection: { collection: rawCollection = [] } = {} } = {},
    loading,
    refetch,
  } = useQuery(LOCATION_SNAPSHOT_ASSETS_QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: {
      ...(isMobileLocation
        ? { mobileWarehouseLocationId: locationDetails?.id || entity.id }
        : { warehouseLocationId: locationDetails?.id || entity.id }),
    },
  });

  const collection = useMemo(() => {
    if (!deletedAssetIds.length) return rawCollection;
    const deletedIds = new Set(deletedAssetIds);
    return rawCollection.filter((asset) => !deletedIds.has(asset.id));
  }, [rawCollection, deletedAssetIds]);

  const matchingShipmentAsset = useMemo(() => {
    if (!itemLabelsModal.shipmentId || !itemLabelsModal.shipmentItemId) {
      return null;
    }

    return collection.find(asset =>
      asset.shipmentItems?.some(shipmentItem =>
        `${shipmentItem?.shipment?.id || ""}` === `${itemLabelsModal.shipmentId}` &&
        `${shipmentItem?.id || ""}` === `${itemLabelsModal.shipmentItemId}`
      )
    ) || null;
  }, [collection, itemLabelsModal.shipmentId, itemLabelsModal.shipmentItemId]);

  const labelingProject = matchingShipmentAsset?.project || null;
  const assetLabelProject = useMemo(() => {
    if (!itemLabelsModal.assetId) return null;

    return collection.find((asset) => `${asset.id}` === `${itemLabelsModal.assetId}`)?.project || null;
  }, [collection, itemLabelsModal.assetId]);

  const visibleAssetIds = useMemo(() => collection.map((asset) => asset.id), [collection]);
  const selectedAssetIdsSet = useMemo(() => new Set(selectedAssetIds), [selectedAssetIds]);
  const selectedAssets = useMemo(
    () => collection.filter((asset) => selectedAssetIdsSet.has(asset.id)),
    [collection, selectedAssetIdsSet]
  );
  const bulkActionsMenuOpen = Boolean(bulkActionsAnchorEl);
  const isBulkActionInProgress = isRemoving || isBulkChangingQuantity || isDeletingAssets;
  const allVisibleSelected =
    visibleAssetIds.length > 0 && visibleAssetIds.every((id) => selectedAssetIdsSet.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleAssetIds.some((id) => selectedAssetIdsSet.has(id));

  const currentLocationName = locationName || entity?.locationName;
  const currentLocationId =
    locationDetails?.locationId || locationDetails?.location_id || locationDetails?.id;
  const hasModelColumn = collection.some((asset) => asset.model);

  const [openSearch, setOpenSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleToggleSearch = () => {
    if (openSearch) setSearchTerm("");
    setOpenSearch((v) => !v);
  };

  const filteredCollection = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return collection;
    return collection.filter((asset) => {
      return (
        asset.itemId?.toString().toLowerCase().includes(term) ||
        asset.description?.toLowerCase().includes(term) ||
        asset.assetId?.toLowerCase().includes(term)
      );
    });
  }, [collection, searchTerm]);

  const MOBILE_LOCATION_COLORS = [
    { bg: "#e3f2fd", text: "#1565c0" },
    { bg: "#e8f5e9", text: "#2e7d32" },
    { bg: "#f3e5f5", text: "#6a1b9a" },
    { bg: "#fff3e0", text: "#e65100" },
    { bg: "#e0f7fa", text: "#00695c" },
    { bg: "#fce4ec", text: "#880e4f" },
    { bg: "#f9fbe7", text: "#558b2f" },
    { bg: "#fff8e1", text: "#f57f17" },
  ];

  const mobileLocationColorMap = useMemo(() => {
    const seen = {};
    let index = 0;
    collection.forEach((asset) => {
      const rls = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];
      const mobileLoc = rls
        .map((rl) => rl.mobile_warehouse_location)
        .find((ml) => ml?.location_name);
      if (mobileLoc?.location_name && !(mobileLoc.location_name in seen)) {
        seen[mobileLoc.location_name] =
          MOBILE_LOCATION_COLORS[index % MOBILE_LOCATION_COLORS.length];
        index++;
      }
    });
    return seen;
  }, [collection]);

  useEffect(() => {
    const visibleIdsSet = new Set(visibleAssetIds);
    setSelectedAssetIds((previousSelectedIds) => {
      const nextSelectedIds = previousSelectedIds.filter((assetId) => visibleIdsSet.has(assetId));
      if (
        nextSelectedIds.length === previousSelectedIds.length &&
        nextSelectedIds.every((assetId, index) => assetId === previousSelectedIds[index])
      ) {
        return previousSelectedIds;
      }

      return nextSelectedIds;
    });
  }, [visibleAssetIds]);

  useEffect(() => {
    if (!auditModeEnabled) {
      setSelectedAssetIds([]);
      setOpenBulkChangeQuantityModal(false);
      setLastSelectedId(null);
    }
  }, [auditModeEnabled]);

  useEffect(() => {
    if (selectedAssetIds.length === 0) {
      setBulkActionsAnchorEl(null);
    }
  }, [selectedAssetIds.length]);

  const getSubQty = (locations) => {
    const safeLocations = Array.isArray(locations) ? locations : [];
    return sumBy(
      safeLocations.filter((v) => {
        const rackWarehouseId = getWarehouseId(v.warehouse);
        const matchesWarehouse =
          !warehouseId || !rackWarehouseId ? true : `${rackWarehouseId}` === `${warehouseId}`;

        if (isMobileLocation) {
          return (
            matchesWarehouse && v.mobile_warehouse_location?.location_name === entity.locationName
          );
        } else {
          const locationNames = (v.warehouse_locations || []).map((loc) => loc.location_name);
          return matchesWarehouse && locationNames.includes(entity.locationName);
        }
      }),
      (v) => v.sub_qty
    );
  };

  const isSameWarehouse = (rackLocation) => {
    const rackWarehouseId = getWarehouseId(rackLocation?.warehouse);
    if (!warehouseId || !rackWarehouseId) {
      return true;
    }

    return `${rackWarehouseId}` === `${warehouseId}`;
  };

  const matchesCurrentWarehouseLocation = (warehouseLocation) => {
    if (!warehouseLocation) {
      return false;
    }

    const warehouseLocationId = getLocationId(warehouseLocation);
    if (currentLocationId && warehouseLocationId) {
      return `${warehouseLocationId}` === `${currentLocationId}`;
    }

    const warehouseLocationName = getLocationName(warehouseLocation);
    return Boolean(currentLocationName && warehouseLocationName === currentLocationName);
  };

  const matchesCurrentMobileLocation = (mobileLocation) => {
    if (!mobileLocation) {
      return false;
    }

    const mobileLocationId = getLocationId(mobileLocation);
    if (currentLocationId && mobileLocationId) {
      return `${mobileLocationId}` === `${currentLocationId}`;
    }

    const mobileLocationName = getLocationName(mobileLocation);
    return Boolean(currentLocationName && mobileLocationName === currentLocationName);
  };

  const removeCurrentLocationFromRackLocations = (rackLocations) => {
    if (!Array.isArray(rackLocations)) {
      return { removedAny: false, nextRackLocations: [] };
    }

    let removedAny = false;
    const nextRackLocations = [];

    rackLocations.forEach((rackLocation) => {
      if (!rackLocation || typeof rackLocation !== "object") {
        return;
      }

      if (!isSameWarehouse(rackLocation)) {
        nextRackLocations.push(rackLocation);
        return;
      }

      if (isMobileLocation) {
        if (matchesCurrentMobileLocation(rackLocation.mobile_warehouse_location)) {
          removedAny = true;
          const warehouseLocations = Array.isArray(rackLocation.warehouse_locations)
            ? rackLocation.warehouse_locations
            : [];
          if (warehouseLocations.length > 0) {
            const { mobile_warehouse_location, ...rackLocationWithoutMobile } = rackLocation;
            nextRackLocations.push(rackLocationWithoutMobile);
          }
          return;
        }

        nextRackLocations.push(rackLocation);
        return;
      }

      const warehouseLocations = Array.isArray(rackLocation.warehouse_locations)
        ? rackLocation.warehouse_locations
        : [];

      if (!warehouseLocations.length) {
        nextRackLocations.push(rackLocation);
        return;
      }

      const remainingWarehouseLocations = warehouseLocations.filter(
        (warehouseLocation) => !matchesCurrentWarehouseLocation(warehouseLocation)
      );

      if (remainingWarehouseLocations.length !== warehouseLocations.length) {
        removedAny = true;
      }

      if (remainingWarehouseLocations.length > 0) {
        nextRackLocations.push({
          ...rackLocation,
          warehouse_locations: remainingWarehouseLocations,
        });
      } else if (rackLocation.mobile_warehouse_location) {
        nextRackLocations.push({
          ...rackLocation,
          warehouse_locations: [],
        });
      }
    });

    return { removedAny, nextRackLocations };
  };

  const setCurrentLocationQuantityInRackLocations = (rackLocations, quantity) => {
    if (!Array.isArray(rackLocations)) {
      return { quantityUpdated: false, nextRackLocations: [] };
    }

    const nextRackLocations = rackLocations.map((rackLocation) => ({ ...rackLocation }));
    let quantityUpdated = false;

    for (const rackLocation of nextRackLocations) {
      if (!rackLocation || typeof rackLocation !== "object") {
        continue;
      }

      const rackWarehouseId = getWarehouseId(rackLocation.warehouse);
      const matchesWarehouse =
        !warehouseId || !rackWarehouseId ? true : `${rackWarehouseId}` === `${warehouseId}`;

      if (!matchesWarehouse) {
        continue;
      }

      if (isMobileLocation) {
        if (matchesCurrentMobileLocation(rackLocation.mobile_warehouse_location)) {
          rackLocation.sub_qty = quantity;
          quantityUpdated = true;
          break;
        }
        continue;
      }

      const rackWarehouseLocations = Array.isArray(rackLocation.warehouse_locations)
        ? rackLocation.warehouse_locations
        : [];
      if (rackWarehouseLocations.some(matchesCurrentWarehouseLocation)) {
        rackLocation.sub_qty = quantity;
        quantityUpdated = true;
        break;
      }
    }

    return { quantityUpdated, nextRackLocations };
  };

  const toggleAssetSelection = (assetId, event) => {
    const isShift = event && event.shiftKey;
    let newSelectedIds;

    if (isShift && lastSelectedId) {
      const start = visibleAssetIds.indexOf(lastSelectedId);
      const end = visibleAssetIds.indexOf(assetId);

      if (start !== -1 && end !== -1) {
        const min = Math.min(start, end);
        const max = Math.max(start, end);
        const range = visibleAssetIds.slice(min, max + 1);
        const selectedSet = new Set(selectedAssetIds);
        range.forEach((id) => selectedSet.add(id));
        newSelectedIds = Array.from(selectedSet);
      }
    }

    if (!newSelectedIds) {
      newSelectedIds = selectedAssetIds.includes(assetId)
        ? selectedAssetIds.filter((id) => id !== assetId)
        : [...selectedAssetIds, assetId];
    }

    setLastSelectedId(assetId);
    setSelectedAssetIds(newSelectedIds);
  };

  const toggleSelectAllVisible = (checked) => {
    if (checked) {
      setSelectedAssetIds((previousSelectedIds) => {
        const mergedIds = new Set([...previousSelectedIds, ...visibleAssetIds]);
        return Array.from(mergedIds);
      });
      return;
    }

    const visibleIdsSet = new Set(visibleAssetIds);
    setSelectedAssetIds((previousSelectedIds) =>
      previousSelectedIds.filter((assetId) => !visibleIdsSet.has(assetId))
    );
  };

  const handleAssetSelected = async (asset) => {
    if (!asset?.id) {
      return false;
    }

    onOpenAsset?.(asset.id);

    const refreshTasks = [refetch()];
    if (onRefetchLocationDetails) {
      refreshTasks.push(onRefetchLocationDetails());
    }
    await Promise.allSettled(refreshTasks);

    return true;
  };

  const submitRackLocationUpdate = async ({
    asset,
    oldRackLocations,
    nextRackLocations,
    actionSource,
    fallbackErrorMessage,
  }) => {
    let mutationFailure = null;
    const response = await createInventoryTransaction({
      variables: {
        code: asset.assetId || asset.itemId || "",
        quantity: 0,
        notes: "",
        senderName: "",
        carrierName: "",
        trackingNumber: "",
        orderNumber: "",
        poNumber: "",
        rackLocations: nextRackLocations,
        rackLocationsChanges: nextRackLocations,
        oldRackLocations,
        assetId: asset.id,
        actionSource,
      },
      onFailure: (apiErrors) => {
        mutationFailure = apiErrors;
      },
    });

    const responseErrors = response?.data?.createInventoryTransaction?.errors;
    const responseErrorMessage = formatMutationErrors(responseErrors);

    if (mutationFailure || responseErrorMessage) {
      return mutationFailure?.submit || responseErrorMessage || fallbackErrorMessage || "";
    }

    return "";
  };

  const openBulkActionsMenu = (event) => {
    setBulkActionsAnchorEl(event.currentTarget);
  };

  const closeBulkActionsMenu = () => {
    setBulkActionsAnchorEl(null);
  };

  const handleOpenBulkRemove = () => {
    closeBulkActionsMenu();
    setOpenRemoveConfirmation(true);
  };

  const handleOpenBulkChangeQuantity = () => {
    closeBulkActionsMenu();
    setOpenBulkChangeQuantityModal(true);
  };

  const handleOpenBulkDeleteAsset = () => {
    closeBulkActionsMenu();
    setOpenDeleteAssetConfirmation(true);
  };

  const handleBulkDeleteAsset = async () => {
    const idsToDelete = [...selectedAssetIds];
    if (!idsToDelete.length) {
      setOpenDeleteAssetConfirmation(false);
      return;
    }

    setOpenDeleteAssetConfirmation(false);
    setIsDeletingAssets(true);

    let mutationFailure = null;
    await bulkRemoveAssets({
      variables: { ids: idsToDelete },
      onFailure: (errors) => {
        mutationFailure = errors;
      },
    });

    setIsDeletingAssets(false);

    if (mutationFailure) {
      notifyError(mutationFailure?.submit || "Failed to delete selected assets.");
      return;
    }

    setSelectedAssetIds([]);
    setDeletedAssetIds((prev) => [...prev, ...idsToDelete]);
    notifySuccess(
      `Deleted ${idsToDelete.length} selected ${idsToDelete.length === 1 ? "asset" : "assets"}.`
    );

    try {
      await refetch();
      setDeletedAssetIds([]);
    } catch (error) {
      console.error(error);
    }
    refetchWarehouse();
  };

  const handleSaveEditQuantity = async (newQty) => {
    if (!editQuantityAsset) return;

    const asset = editQuantityAsset;
    const oldRackLocations = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];
    const currentQty = getSubQty(oldRackLocations);

    if (newQty === currentQty) {
      setEditQuantityAsset(null);
      return;
    }

    const { quantityUpdated, nextRackLocations } = setCurrentLocationQuantityInRackLocations(
      oldRackLocations,
      newQty
    );
    if (!quantityUpdated) {
      notifyError("Could not find matching rack location to update.");
      return;
    }

    setIsSavingQuantity(true);
    const mutationErrorMessage = await submitRackLocationUpdate({
      asset,
      oldRackLocations,
      nextRackLocations,
      actionSource: "location_snapshot_audit_edit_qty",
      fallbackErrorMessage: "Failed to update quantity.",
    });
    setIsSavingQuantity(false);

    if (mutationErrorMessage) {
      notifyError(mutationErrorMessage);
      return;
    }

    setEditQuantityAsset(null);
    notifySuccess("Location quantity updated successfully.");
    refetch();
    refetchWarehouse();
  };

  const handleBulkChangeQuantity = async (quantity) => {
    const assetsToProcess = [...selectedAssets];
    if (!assetsToProcess.length) {
      setOpenBulkChangeQuantityModal(false);
      return;
    }

    setOpenBulkChangeQuantityModal(false);
    setIsBulkChangingQuantity(true);

    let updatedCount = 0;
    let unchangedCount = 0;
    const failedAssetIds = [];
    const errors = [];

    try {
      for (const asset of assetsToProcess) {
        const oldRackLocations = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];
        const currentQty = getSubQty(oldRackLocations);

        if (currentQty === quantity) {
          unchangedCount += 1;
          continue;
        }

        const { quantityUpdated, nextRackLocations } = setCurrentLocationQuantityInRackLocations(
          oldRackLocations,
          quantity
        );
        if (!quantityUpdated) {
          failedAssetIds.push(asset.id);
          errors.push({
            assetId: getAssetIdentifier(asset),
            message: "Could not find matching rack location to update.",
          });
          continue;
        }

        const mutationErrorMessage = await submitRackLocationUpdate({
          asset,
          oldRackLocations,
          nextRackLocations,
          actionSource: "location_snapshot_audit_bulk_edit_qty",
          fallbackErrorMessage: "Failed to update quantity.",
        });

        if (mutationErrorMessage) {
          failedAssetIds.push(asset.id);
          errors.push({
            assetId: getAssetIdentifier(asset),
            message: mutationErrorMessage,
          });
          continue;
        }

        updatedCount += 1;
      }
    } finally {
      setIsBulkChangingQuantity(false);
    }

    setSelectedAssetIds(failedAssetIds);

    try {
      await refetch();
    } catch (error) {
      notifyError("Items were processed, but the snapshot refresh failed.");
    }
    refetchWarehouse();

    const failedCount = errors.length;
    const successfulCount = updatedCount + unchangedCount;

    if (failedCount === 0) {
      if (updatedCount === 0) {
        notifySuccess(
          `All selected ${assetsToProcess.length === 1 ? "item already has" : "items already have"} quantity ${quantity}.`
        );
      } else {
        notifySuccess(
          `Updated quantity to ${quantity} for ${successfulCount} selected ${
            successfulCount === 1 ? "item" : "items"
          }.`
        );
      }
    } else if (successfulCount > 0) {
      notifyError(
        `Updated ${successfulCount} of ${assetsToProcess.length}. ${failedCount} failed.`
      );
    } else {
      notifyError("Failed to update quantity for selected items.");
    }
  };

  const handleBulkRemove = async () => {
    const assetsToProcess = [...selectedAssets];
    if (!assetsToProcess.length) {
      setOpenRemoveConfirmation(false);
      return;
    }

    setOpenRemoveConfirmation(false);
    setIsRemoving(true);
    setRemoveProgress({ current: 0, total: assetsToProcess.length });

    const errors = [];
    let successCount = 0;

    try {
      for (let i = 0; i < assetsToProcess.length; i += 1) {
        const asset = assetsToProcess[i];
        setRemoveProgress({ current: i + 1, total: assetsToProcess.length });

        const oldRackLocations = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];
        const { removedAny, nextRackLocations } =
          removeCurrentLocationFromRackLocations(oldRackLocations);

        if (!removedAny) {
          errors.push({
            assetId: getAssetIdentifier(asset),
            message: "Item is no longer assigned to this location.",
          });
          continue;
        }

        let mutationFailure = null;
        const response = await createInventoryTransaction({
          variables: {
            code: asset.assetId || asset.itemId || "",
            quantity: 0,
            notes: "",
            senderName: "",
            carrierName: "",
            trackingNumber: "",
            orderNumber: "",
            poNumber: "",
            rackLocations: nextRackLocations,
            rackLocationsChanges: nextRackLocations,
            oldRackLocations,
            assetId: asset.id,
            actionSource: "location_snapshot_audit_remove",
          },
          onFailure: (apiErrors) => {
            mutationFailure = apiErrors;
          },
        });

        const responseErrors = response?.data?.createInventoryTransaction?.errors;
        const responseErrorMessage = formatMutationErrors(responseErrors);

        if (mutationFailure || responseErrorMessage) {
          errors.push({
            assetId: getAssetIdentifier(asset),
            message: mutationFailure?.submit || responseErrorMessage || "Failed to remove item.",
          });
          continue;
        }

        successCount += 1;
      }
    } finally {
      setIsRemoving(false);
    }

    setSelectedAssetIds([]);

    try {
      await refetch();
    } catch (error) {
      notifyError("Items were processed, but the snapshot refresh failed.");
    }
    refetchWarehouse();

    const failedCount = errors.length;
    if (failedCount === 0) {
      notifySuccess(
        `Removed ${successCount} selected ${
          successCount === 1 ? "item" : "items"
        } from this location.`
      );
    } else if (successCount > 0) {
      notifyError(`Removed ${successCount} of ${assetsToProcess.length}. ${failedCount} failed.`);
    } else {
      notifyError("Failed to remove selected items from this location.");
    }
  };

  const renderDimensions = collection.some(
    (asset) =>
      Array.isArray(asset.dimensions) &&
      asset.dimensions.length > 0 &&
      asset.dimensions.some((dim) => Object.keys(dim).length > 0)
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={1} sx={{ gap: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight={500} fontSize={20} mb={0.5}>
            Location Snapshot
          </Typography>
          <Badge
            badgeContent={collection.length || 0}
            max={999999}
            sx={{
              "& .MuiBadge-badge": {
                position: "relative",
                transform: "none",
                backgroundColor: "transparent",
                color: "#666",
                fontSize: "14px",
                height: "24px",
                minWidth: "24px",
                borderRadius: "50%",
                fontWeight: 500,
                border: "1.5px solid #999",
              },
            }}
          />
          <Tooltip title={openSearch ? "Close Search" : "Search Items"} arrow>
            <IconButton
              onClick={handleToggleSearch}
              size="small"
              aria-label="search-location-snapshot"
            >
              {openSearch ? <CloseIcon /> : <SearchIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refetch Items in This Location" arrow>
            <span>
              <IconButton
                onClick={() => refetch()}
                size="small"
                disabled={loading || isBulkActionInProgress || auditModeLoading}
                aria-label="refresh-location-snapshot"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box flex={1} display="flex" justifyContent="center" alignItems="center" minWidth={0}>
          {auditModeEnabled && selectedAssetIds.length > 0 && (
            <>
              <Badge badgeContent={selectedAssetIds.length} color="primary" max={999999}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={openBulkActionsMenu}
                  disabled={isBulkActionInProgress || auditModeLoading}
                  endIcon={
                    isBulkActionInProgress ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )
                  }
                >
                  Bulk Actions
                </Button>
              </Badge>
              <Menu
                anchorEl={bulkActionsAnchorEl}
                open={bulkActionsMenuOpen}
                onClose={closeBulkActionsMenu}
              >
                <MenuItem
                  onClick={handleOpenBulkRemove}
                  disabled={isBulkActionInProgress || auditModeLoading}
                >
                  Remove from location
                </MenuItem>
                <MenuItem
                  onClick={handleOpenBulkChangeQuantity}
                  disabled={isBulkActionInProgress || auditModeLoading}
                >
                  Change Quantity
                </MenuItem>
                <MenuItem
                  onClick={handleOpenBulkDeleteAsset}
                  disabled={isBulkActionInProgress || auditModeLoading}
                >
                  {selectedAssetIds.length > 1 ? "Remove Assets" : "Remove Asset"}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} flexWrap="wrap">
          {auditModeEnabled ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={openAddItemModal}
              disabled={!locationDetails?.id || isBulkActionInProgress || auditModeLoading}
              startIcon={<AddIcon />}
            >
              Add Item
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setAuditModeEnabled(true)}
              disabled={!locationDetails?.id || isBulkActionInProgress || auditModeLoading}
              startIcon={
                auditModeLoading ? (
                  <CircularProgress size={20} sx={{ color: "primary.main" }} />
                ) : null
              }
            >
              Audit Mode
            </Button>
          )}
        </Box>
      </Box>
      {openSearch && (
        <Box mb={1.5}>
          <TextField
            fullWidth
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      {loading ? (
        <Box mt={2}>
          <LinearProgress />
        </Box>
      ) : filteredCollection.length === 0 ? (
        <Box
          mt={1}
          p={3}
          bgcolor="#f4f6f8"
          borderRadius="4px"
          display="flex"
          justifyContent="center"
        >
          {collection.length === 0 ? "Location is Currently Empty" : "No items match your search"}
        </Box>
      ) : (
        <ProjectInventoryProvider>
          {isMobile ? (
            <LocationSnapshotMobileView
              collection={filteredCollection}
              locationName={locationName}
              renderDimensions={renderDimensions}
              getSubQty={getSubQty}
              onOpenAsset={(assetId) => onOpenAsset?.(assetId)}
              onOpenLabelModal={handleOpenLabelModal}
              auditModeEnabled={auditModeEnabled}
              selectedAssetIdsSet={selectedAssetIdsSet}
              allVisibleSelected={allVisibleSelected}
              someVisibleSelected={someVisibleSelected}
              onToggleSelectAll={toggleSelectAllVisible}
              onToggleAssetSelection={toggleAssetSelection}
              onEditQuantity={setEditQuantityAsset}
              isSavingQuantity={isSavingQuantity || isBulkActionInProgress || auditModeLoading}
              isRemoving={isBulkActionInProgress || auditModeLoading}
            />
          ) : (
            <Table aria-label="Location Snapshot Table">
              <TableHead>
                <TableRow>
                  {auditModeEnabled && (
                    <TableCell align="center" padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={(event) => toggleSelectAllVisible(event.target.checked)}
                        disabled={isBulkActionInProgress || auditModeLoading}
                        inputProps={{ "aria-label": "select-all-assets" }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">Image</TableCell>
                  <TableCell align="center">Item ID</TableCell>
                  <TableCell align="center">Description</TableCell>
                  {hasModelColumn && <TableCell align="center">Model</TableCell>}
                  {renderDimensions && (
                    <TableCell align="center">
                      Dimensions
                      <br />W x D x H
                    </TableCell>
                  )}
                  <TableCell align="center">Total Quantity</TableCell>
                  <TableCell align="center" sx={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCollection.map((asset) => {
                  const rackLocations = Array.isArray(asset.rackLocations)
                    ? asset.rackLocations
                    : [];
                  // Only look at rackLocation entries that contain the current location,
                  // then find what other locations that same entry is also assigned to.
                  const sharedLocations = rackLocations
                    .filter((rl) =>
                      (rl.warehouse_locations || []).some(
                        (loc) => loc.location_name === currentLocationName
                      )
                    )
                    .flatMap((rl) => (rl.warehouse_locations || []).map((loc) => loc.location_name))
                    .filter((name) => name !== currentLocationName)
                    .filter(Boolean);

                  const mobileLoc = rackLocations
                    .map((rl) => rl.mobile_warehouse_location)
                    .find((ml) => ml?.location_name);

                  return (
                    <TableRow key={asset.id} id={`snapshot-${asset.id}`}>
                      {auditModeEnabled && (
                        <TableCell align="center" padding="checkbox">
                          <Checkbox
                            checked={selectedAssetIdsSet.has(asset.id)}
                            onClick={(event) => toggleAssetSelection(asset.id, event)}
                            disabled={isBulkActionInProgress || auditModeLoading}
                            inputProps={{ "aria-label": `select-${asset.itemId || asset.id}` }}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {asset?.photo ? (
                          <img style={{ maxWidth: 100 }} src={asset.photo} alt="asset image" />
                        ) : (
                          <Box
                            sx={{
                              width: 100,
                              height: 100,
                              bgcolor: "#f4f6f8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                              margin: "0 auto",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{ cursor: "pointer" }}
                          color="primary.main"
                          className="unfocus"
                          onClick={() => onOpenAsset?.(asset.id)}
                        >
                          {asset.itemId}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {asset.description}
                        {mobileLoc?.location_name &&
                          mobileLocationColorMap[mobileLoc.location_name] && (
                            <Box mt={0.75}>
                              <Chip
                                label={mobileLoc.location_name}
                                sx={{
                                  backgroundColor:
                                    mobileLocationColorMap[mobileLoc.location_name].bg,
                                  color: mobileLocationColorMap[mobileLoc.location_name].text,
                                  fontWeight: 500,
                                  fontSize: "0.85rem",
                                  height: 28,
                                  width: "100%",
                                  borderRadius: "10px",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                            </Box>
                          )}
                      </TableCell>

                      {hasModelColumn ? (
                        asset.model ? (
                          <TableCell align="center">{asset.model}</TableCell>
                        ) : (
                          <TableCell align="center" />
                        )
                      ) : null}

                      {renderDimensions ? (
                        Array.isArray(asset.dimensions) &&
                        asset.dimensions.length > 0 &&
                        asset.dimensions.some((dim) => Object.keys(dim).length > 0) ? (
                          <TableCell align="center">
                            <Dimensions dimensions={asset.dimensions} />
                          </TableCell>
                        ) : (
                          <TableCell align="center" />
                        )
                      ) : null}

                      <TableCell align="center">
                        {auditModeEnabled ? (
                          <TextField
                            disabled
                            value={getSubQty(rackLocations)}
                            size="small"
                            sx={{ maxWidth: 110 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Edit Location Quantity" placement="top">
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      onClick={() => setEditQuantityAsset(asset)}
                                      disabled={
                                        isSavingQuantity ||
                                        isBulkActionInProgress ||
                                        auditModeLoading
                                      }
                                      aria-label={`edit-qty-${asset.itemId || asset.id}`}
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        ) : (
                          getSubQty(rackLocations)
                        )}
                        {sharedLocations.length > 0 && (
                          <Box mt={0.5} fontSize="0.90rem" color="error.main">
                            This quantity is split with: {sharedLocations.join(", ")}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Generate Label PDF" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenLabelModal(asset)}
                            aria-label={`pdf-${asset.itemId || asset.id}`}
                          >
                            <PictureAsPdfIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ProjectInventoryProvider>
      )}
      <LocationSnapshotAddItemModal
        open={openAddItem}
        onClose={closeAddItemModal}
        locationName={locationName}
        locationDetails={locationDetails}
        isMobileLocation={isMobileLocation}
        onAssetSelected={handleAssetSelected}
      />
      {openRemoveConfirmation && (
        <ConfirmDeleteModal
          title="Confirm Removal"
          descr={`Remove ${selectedAssets.length} selected ${
            selectedAssets.length === 1 ? "item" : "items"
          } from this location?`}
          onClose={() => setOpenRemoveConfirmation(false)}
          onDelete={handleBulkRemove}
          applyLabel="Confirm"
        />
      )}
      {openDeleteAssetConfirmation && (
        <ConfirmDeleteModal
          title="Delete Assets"
          descr={`Are you sure you want to delete ${selectedAssets.length} selected ${
            selectedAssets.length === 1 ? "asset" : "assets"
          }? This action cannot be undone.`}
          onClose={() => setOpenDeleteAssetConfirmation(false)}
          onDelete={handleBulkDeleteAsset}
        />
      )}
      {openBulkChangeQuantityModal && (
        <LocationSnapshotBulkChangeQuantityModal
          selectedCount={selectedAssets.length}
          saving={isBulkChangingQuantity}
          onClose={() => setOpenBulkChangeQuantityModal(false)}
          onApply={handleBulkChangeQuantity}
        />
      )}
      {editQuantityAsset && (
        <LocationSnapshotEditQuantityModal
          asset={editQuantityAsset}
          currentQuantity={getSubQty(
            Array.isArray(editQuantityAsset.rackLocations) ? editQuantityAsset.rackLocations : []
          )}
          locationName={currentLocationName}
          saving={isSavingQuantity}
          onSave={handleSaveEditQuantity}
          onClose={() => setEditQuantityAsset(null)}
        />
      )}
      {itemLabelsModal.isOpen && matchingShipmentAsset && labelingProject && (
        <LabelingOptionsModal
          onClose={itemLabelsModal.close}
          shipmentId={itemLabelsModal.shipmentId}
          shipmentItemId={itemLabelsModal.shipmentItemId}
          project={labelingProject}
        />
      )}
      {itemLabelsModal.isOpen && itemLabelsModal.assetId && (
        <AssetLabelingOptionsModal
          onClose={itemLabelsModal.close}
          assetId={itemLabelsModal.assetId}
          project={assetLabelProject}
        />
      )}
    </Paper>
  );
}
